import Button from '@components/Button';
import Checkbox from '@components/Checkbox';
import { CollapseWrapper } from '@components/Collapse';
import { showApplyWarning, showConfirmModal } from '@components/ConfirmModal';
import { sendSuccess } from '@components/Noti';
import SortableList from '@components/SortableList';
import Tip from '@components/Tip';
import { TipWrapper } from '@components/TipWrapper';
import Title from '@components/Title';
import IconBatch from '@components/icons/IconBatch';
import useToggle from '@hooks/useToggle';
import { $sandTable, $sceneMapApi } from '@stores/dataStores';
import { $GateSandTableId } from '@stores/editingStores';
import { $loadedScene } from '@stores/loadedStore';
import arrayMove from '@utils/arrayMove';
import getSelectedStatus from '@utils/getSelectedStatus';
import { useGate, useStore } from 'effector-react';
import { useCallback, useMemo, useState } from 'react';
import { Scrollbars } from 'react-custom-scrollbars';
import { useNavigate } from 'react-router-dom';
import type { SortEnd } from 'react-sortable-hoc';
import { useImmer } from 'use-immer';
import { showSceneModal } from '../../category';
import { showSandTableModal } from '../../sandTable/SandTableModal';
import { SandTableListItem } from '../../sandTable/components/SandTableListItem';
import SidebarApplyTo from '../components/SidebarApplyTo/SidebarApplyTo';
import SidebarHeader from '../components/SidebarHeader';
import { BatchModeItem } from './BatchModeItem';

const MAX = 10;

export function SidebarSceneSandTable() {
  const scene = useStore($loadedScene)!;
  const sandTable = useStore($sandTable);
  const { list: origList = [], defaultId } = scene.sandBox ?? { list: [] };

  // 过滤沙盘id (分场景保存可能导致的问题)
  const list = useMemo(() => {
    return origList.filter((id) => sandTable.find((x) => x.id === id));
  }, [origList, sandTable]);

  const [activeId, setActiveId] = useState<string | null>(() => {
    if (defaultId && list.includes(defaultId)) return defaultId;
    return list?.[0] ?? null;
  });

  const navigate = useNavigate();
  const [selected, setSelected] = useImmer<Set<string>>(new Set());
  const batchMode = useToggle(false);
  const [deleteDisabled, setDeleteDisabled] = useState(false);

  useGate($GateSandTableId, batchMode.isOn ? null : activeId);

  const handleAddClick = async () => {
    const { idList } = await showSandTableModal({
      max: MAX,
      selected: list,
      navigate,
      backTab: 'sandTable',
    });
    if (!idList) return;
    $sceneMapApi.updateScene({
      id: scene.id,
      updater: (draft) => {
        draft.sandBox ??= { list: [] };
        if (!idList.includes(defaultId!)) {
          draft.sandBox.defaultId = idList[0];
        }
        draft.sandBox.list = idList;
      },
    });
    if (!idList.includes(activeId!)) {
      setActiveId(idList[0]);
    }
  };

  const handleSortEnd = (sort: SortEnd) => {
    $sceneMapApi.updateScene({
      id: scene.id,
      updater: (draft) => {
        draft.sandBox.list = arrayMove({
          array: draft.sandBox.list ?? [],
          fromIndex: sort.oldIndex,
          toIndex: sort.newIndex,
        });
      },
    });
  };

  const handleItemClick = (id: string) => {
    setActiveId(id);
  };
  const handleItemDelete = (id: string) => {
    setDeleteDisabled(true);
    const newList = list.filter((item) => item !== id);
    $sceneMapApi.updateScene({
      id: scene.id,
      updater: (draft) => {
        if (id === defaultId) {
          draft.sandBox.defaultId = newList[0];
        }
        draft.sandBox.list = newList;
      },
    });
    if (id === activeId) {
      setActiveId(newList[0] ?? null);
    }
    setTimeout(() => {
      setDeleteDisabled(false);
    }, 500);
  };

  const handleItemSetDefault = (id: string) => {
    $sceneMapApi.updateScene({
      id: scene.id,
      updater: (draft) => {
        draft.sandBox.defaultId = id;
      },
    });
  };

  const handleSelectAll = () => {
    if (selected.size < list.length) {
      setSelected(new Set(list));
    } else {
      setSelected((draft) => {
        draft.clear();
      });
    }
  };

  const handleBatchItemClick = useCallback((id: string) => {
    setSelected((draft) => {
      if (draft.has(id)) {
        draft.delete(id);
      } else {
        draft.add(id);
      }
    });
  }, []);

  const handleBatchDelete = async () => {
    const { confirmed } = await showConfirmModal({
      title: '操作确认',
      content: (
        <div className="mb-5 text-xl">{`确认删除选中的${selected.size}个沙盘吗？`}</div>
      ),
    });
    if (!confirmed) return;
    const newList = list.filter((item) => !selected.has(item));
    $sceneMapApi.updateScene({
      id: scene.id,
      updater: (draft) => {
        if (selected.has(activeId!)) {
          draft.sandBox.defaultId = newList[0];
        }
        draft.sandBox.list = newList;
      },
    });
    if (selected.has(activeId!)) {
      setActiveId(newList[0] ?? null);
    }
    setSelected((draft) => {
      draft.clear();
    });
  };

  const handleApplyTo = async () => {
    if (!list.length) {
      const { confirmed } = await showApplyWarning();
      if (!confirmed) return;
    }
    const { idSet } = await showSceneModal({
      title: '选择要应用到的场景',
      multiple: true,
      disabledIds: [scene.id],
    });
    if (!idSet) return;
    $sceneMapApi.update((draft) => {
      Array.from(idSet).forEach((_id) => {
        const item = draft.get(_id);
        if (!item) return;
        item.sandBox.defaultId = defaultId;
        item.sandBox.list = list;
      });
    });
    sendSuccess('场景沙盘已应用到所选场景');
  };

  const renderList = () => {
    return (
      <>
        <Button
          size="lg"
          className="mb-10 w-full"
          disabled={list.length >= MAX}
          onClick={handleAddClick}
        >
          添加沙盘
        </Button>
        {list.length > 0 ? (
          <div className="relative flex-1">
            <SortableList
              axis="y"
              lockAxis="y"
              distance={10}
              onSortEnd={handleSortEnd}
            >
              <Scrollbars>
                {list.map((stId, i) => (
                  <SandTableListItem
                    key={stId}
                    id={stId}
                    index={i}
                    isSelected={stId === activeId}
                    isDefault={stId === defaultId}
                    deleteDisabled={deleteDisabled}
                    className="bg-black-#28 hover:bg-black-#34"
                    onClick={handleItemClick}
                    onDeleteClick={handleItemDelete}
                    onSetDefaultClick={handleItemSetDefault}
                  />
                ))}
              </Scrollbars>
            </SortableList>
          </div>
        ) : (
          <div className="flex-1">
            <Tip className="mt-5 text-center text-base">{`最多可添加${MAX}个沙盘`}</Tip>
            <Tip className="mt-5 text-center text-base">
              添加后支持上下拖拽调整沙盘顺序
            </Tip>
          </div>
        )}
        <SidebarApplyTo
          className="mt-10"
          title="把场景沙盘设置应用到..."
          onClick={handleApplyTo}
        />
      </>
    );
  };

  const renderBatchModeList = () => {
    return (
      <>
        <div
          className="mb-10 flex h-35 cursor-pointer items-center rounded bg-black-#34 px-10"
          onClick={handleSelectAll}
        >
          <Checkbox
            status={getSelectedStatus(selected.size, list.length)}
            onClick={handleSelectAll}
          />
          <div className="ml-5">全选</div>
          <div className="ml-auto text-sm text-black-gray ">{`${selected.size}/${list.length}`}</div>
        </div>
        {list.length > 0 ? (
          <div className="relative flex-1">
            <Scrollbars>
              {list.map((sId) => (
                <BatchModeItem
                  key={sId}
                  id={sId}
                  isSelected={selected.has(sId)}
                  onClick={handleBatchItemClick}
                />
              ))}
            </Scrollbars>
          </div>
        ) : (
          <Tip className="mt-5 flex-1 text-center text-base">
            没有沙盘可批量操作
          </Tip>
        )}
        {batchMode && (
          <>
            <Button
              disabled={!selected.size}
              className="mt-10 w-full"
              onClick={handleBatchDelete}
            >
              删除
            </Button>
            <Button
              scheme="darkest"
              className="mt-10 w-full"
              onClick={batchMode.off}
            >
              退出
            </Button>
          </>
        )}
      </>
    );
  };

  return (
    <CollapseWrapper>
      <div className="absolute inset-0 flex flex-col">
        <SidebarHeader
          title={
            <div className="flex">
              <Title>场景沙盘</Title>
              <Tip className="ml-10 self-end text-base">
                {batchMode.isOn ? '批量操作' : `${list.length}/${MAX}`}
              </Tip>
            </div>
          }
          btns={
            <TipWrapper
              text={batchMode.isOn ? '退出批量操作' : '批量操作'}
              size="sm"
              placement="bottom"
            >
              <IconBatch
                className="focus:outline-none"
                active={batchMode.isOn}
                onClick={batchMode.toggle}
              />
            </TipWrapper>
          }
        />
        {!batchMode.isOn && renderList()}
        {batchMode.isOn && renderBatchModeList()}
      </div>
    </CollapseWrapper>
  );
}
